import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import "./LeftControlPanel.scss";
import { useCreatePlaylistStore } from "../../../stores";
import PlaylistSettings from "./PlaylistSettings";
import {
    Button,
    IconEdit,
    Modal,
    ModalConfirm,
    useNotification,
    IconCloseX, 
    IconImageFile, 
    IconScenarioFile, 
    IconVideoFile
} from "../../../components/UI";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18nano";
import AddPlaylistItems from "./AddPlaylistItems";
import ContentContainer from "../../../components/ContentContainer";
import { TPlaylistItemType } from "../../../types/playlist";


const LeftControlPanel = () => {
    const {
        playlist,
        createPlaylist,
        changeIndexPlaylistItem,
        currentPlaylistItem,
        deletePlaylistItem,
        setCurrentPlaylist
    } = useCreatePlaylistStore(state => state);
    const { addNotific } = useNotification();
    const t = useTranslation();

    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [saveOnChange, setSaveOnChange] = useState(false);

    const [modalContent, setModalContent] = useState<JSX.Element>(<></>);
    const [modalTitle, setModalTitle] = useState({
        title: "",
        subtitle: ""
    });

    const createPlaylistHandler = () => {
        if (playlist.name.length === 0) {
            setModalContent(<PlaylistSettings setShowModal={setShowModal} />);
            setModalTitle({ title: t("button.settings"), subtitle: "" });
            setSaveOnChange(true);
            setShowModal(true);
            return;
        }
        if (playlist.playlist_items.length === 0) {
            addNotific({
                title: t("notification.error.label"),
                body: t("notification.error.create_playlsit.left_control_panel.missing_items"),
                type: "danger"
            });
            return;
        }

        createPlaylist().then(val => setShowConfirm(val));
    };

    const showModalHandler = (modalContentType: string) => {
        if (modalContentType === "addContent") {
            setModalContent(<AddPlaylistItems setShowModal={setShowModal} />);
            setModalTitle({ title: t("create_playlist.add_playlist_item.title"), subtitle: t("create_playlist.add_playlist_item.subtitle") });
            setShowModal(true);
        } else if (modalContentType === "settingsPlaylist") {
            setModalContent(<PlaylistSettings setShowModal={setShowModal} />);
            setModalTitle({ title: t("button.settings"), subtitle: "" });
            setShowModal(true);
        }
    };

    const onCancel = () => {
        navigate("/playlists");
    };

    useEffect(() => {
        if (saveOnChange && playlist.name.length) {
            setSaveOnChange(false);
            createPlaylistHandler();
        }
    }, [playlist.name]);

    const deletePlaylistItemHandler = (deleteId?: string) => {
        if (deleteId) {
            deletePlaylistItem(deleteId);
        } else {
            if (currentPlaylistItem) {
                deletePlaylistItem(currentPlaylistItem.id);
            } else {
                addNotific({
                    title: t("notification.error.label"),
                    body: t("notification.error.create_playlsit.left_control_panel.nothing_delete"),
                    type: "danger"
                });
            }
        }
    };

    const icons = (content: TPlaylistItemType, size: number) => {
        switch (content) {
            case "image":
                return <IconImageFile size={size} color={"black"} />;
            case "scenario":
                return <IconScenarioFile size={size} color={"black"} />;
            case "video":
                return <IconVideoFile size={size} color={"black"} />;
        }
    };

    const dragPlaylistItem = useRef<number>(0);
    const draggedOverPlaylistItem = useRef<number>(0);

    function handlePlaylistItemsSort() {
        changeIndexPlaylistItem(draggedOverPlaylistItem.current, dragPlaylistItem.current)
    }

    function handleOnDragOver(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();
        event.stopPropagation();

        if (dragPlaylistItem.current > draggedOverPlaylistItem.current) {
            event.currentTarget.classList.add("dragoverUp");
        }
        if (dragPlaylistItem.current < draggedOverPlaylistItem.current) {
            event.currentTarget.classList.add("dragoverDown");
        }
    }

    function handleOnDragLeave(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();
        event.stopPropagation();

        event.currentTarget.classList.remove("dragoverUp");
        event.currentTarget.classList.remove("dragoverDown");
    }

    return (
        <div className={"controlPanel leftControlPanel"}>
            <div className={"controlPanelTop"}>
                <div className={"name-container"}>
                    <span className="name">{playlist.name.length ? playlist.name : t("create_playlist.input_playlist_name")}</span>
                    <span className="icon" onClick={() => showModalHandler("settingsPlaylist")}><IconEdit size={22} /></span>
                </div>
                <div className={"add-content-button-container"}>
                    <Button variant={"primary"} onClick={() => showModalHandler("addContent")} >{t("create_playlist.add_playlist_item.title")}</Button>
                </div>
                <div className={"playlistItemsContainer"}>
                    {playlist.playlist_items.map((playlistItem, index) =>
                        <div
                            draggable 
                            className={
                                classnames(
                                    "playlistItem", 
                                    { "active": currentPlaylistItem?.id === playlistItem.id }
                                )
                            }
                            onClick={() => setCurrentPlaylist(playlistItem.id)}
                            onDragStart={() => (dragPlaylistItem.current = index)}
                            onDragEnter={() => (draggedOverPlaylistItem.current = index)}
                            onDragOver={(e) => (handleOnDragOver(e))}
                            onDragLeave={(e) => (handleOnDragLeave(e))}
                            onDrop={(e) => (handleOnDragLeave(e))}
                            onDragEnd={handlePlaylistItemsSort}
                        >
                            <div className={classnames("previewContainer")}>
                                {playlistItem.preview.length
                                    ? <>
                                        <ContentContainer url={playlistItem.preview} contentType={"preview"} />
                                        <span style={{
                                            background: playlistItem.type === "video" ? "rgba(0, 96, 174, 0.6)" :
                                                playlistItem.type === "image" ? "rgba(222, 134, 26, 0.6)" :
                                                    "rgba(222, 26, 140, 0.3)"
                                        }}>{icons(playlistItem.type, 15)}</span>
                                    </>
                                    : <>
                                        {icons(playlistItem.type, 50)}
                                        <span style={{
                                            background: playlistItem.type === "video" ? "rgba(0, 96, 174, 0.6)" :
                                                playlistItem.type === "image" ? "rgba(222, 134, 26, 0.6)" :
                                                    "rgba(222, 26, 140, 0.3)"
                                        }}>{icons(playlistItem.type, 15)}</span>
                                    </>
                                }
                            </div>
                            <span className={"playlistItemName"}>
                                {index + 1}. {playlistItem.name}
                            </span>
                            <span className="deleteIcon" onClick={() => deletePlaylistItemHandler(playlistItem.id)}>
                                <IconCloseX color={"black"} size={20} />
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className={"controlPanelBottom"}>
                <Button onClick={() => createPlaylistHandler()} variant={"primary"}>{t("button.save")}</Button>
                <Button onClick={onCancel} variant={"secondary"}>{t("button.cancel")}</Button>
            </div>
            <Modal isShow={showModal} setIsShow={setShowModal} title={modalTitle.title} subtitle={modalTitle.subtitle}>
                {modalContent}
            </Modal>
            <ModalConfirm isShow={showConfirm} setIsShow={setShowConfirm} onConfirm={() => setSaveOnChange(false)} onCancel={onCancel} okText={t("button.yes")} cancleText={t("button.no")}>
                <p>{t("confirm.create", { name: t("playlist.one") })}</p>
            </ModalConfirm>
        </div>
    );
};

export default LeftControlPanel;